import type { AggIssueField as AggField } from '@atlassian/jira-issue-agg-field-transformers/src/common/types/issue-type.tsx';
import { transformAggFieldToLegacyField } from '@atlassian/jira-issue-agg-field-transformers/src/common/utils/fields-transformer.tsx';
import {
	DATE_PICKER_GQL_FIELD,
	PEOPLE_GQL_FIELD,
	NUMBER_GQL_FIELD,
	PRIORITY_GQL_FIELD,
	USER_PICKER_GQL_FIELD,
	SPRINT_GQL_FIELD,
	TEXT_GQL_FIELD,
	PARENT_GQL_FIELD,
	PROJECT_GQL_FIELD,
	DATE_TIME_GQL_FIELD,
	URL_GQL_FIELD,
	SECURITY_GQL_FIELD,
	CHECKBOX_GQL_FIELD,
	COLOR_GQL_FIELD,
	COMPONENTS_GQL_FIELD,
	ORIGINAL_ESTIMATE_GQL_FIELD,
	TIME_TRACKING_GQL_FIELD,
	WATCHES_GQL_FIELD,
	MULTI_USER_PICKER_GQL_FIELD,
	VOTES_GQL_FIELD,
	ISSUE_TYPE_GQL_FIELD,
	RESOLUTION_GQL_FIELD,
	MULTI_GROUP_PICKER_GQL_FIELD,
	SINGLE_GROUP_PICKER_GQL_FIELD,
	TEAM_VIEW_GQL_FIELD,
	STATUS_GQL_FIELD,
	RADIO_GQL_FIELD,
	RICH_TEXT_GQL_FIELD,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { AggFieldChangeProps, FieldValue } from '../../types.tsx';
import { transformAggParentFieldValueToLegacyFieldValue } from './parent/index.tsx';
import { transformAggRichTextFieldValueToLegacyFieldValue } from './rich-text/index.tsx';

type TransformAggProps = {
	fieldValue: FieldValue;
	aggFieldChangeProps: AggFieldChangeProps;
};

export const transformAggToLegacy = ({ fieldValue, aggFieldChangeProps }: TransformAggProps) => {
	if (!aggFieldChangeProps?.__typename) {
		throw new Error('node did not have __typename');
	}
	switch (aggFieldChangeProps.__typename) {
		// These fields require context for their transform in transformAggFieldToLegacyField so need to be manually implemented
		// This is not an exhaustive list, I did a quick check is all, so best to check the transform works as expected as you migrate fields to relay
		// These are commented out because they are not implemented and due to types can't just be added here until implemented
		// case JSM_PEOPLE_GQL_FIELD:
		// case ISSUE_RESTRICTION_GQL_FIELD:

		// otherwise we can default to the existing package
		case RADIO_GQL_FIELD:
		case TEAM_VIEW_GQL_FIELD:
		case MULTI_GROUP_PICKER_GQL_FIELD:
		case SINGLE_GROUP_PICKER_GQL_FIELD:
		case MULTI_USER_PICKER_GQL_FIELD:
		case PEOPLE_GQL_FIELD:
		case SECURITY_GQL_FIELD:
		case NUMBER_GQL_FIELD:
		case TEXT_GQL_FIELD:
		case USER_PICKER_GQL_FIELD:
		case PRIORITY_GQL_FIELD:
		case URL_GQL_FIELD:
		case DATE_PICKER_GQL_FIELD:
		case PROJECT_GQL_FIELD:
		case DATE_TIME_GQL_FIELD:
		case COLOR_GQL_FIELD:
		case ORIGINAL_ESTIMATE_GQL_FIELD:
		case ISSUE_TYPE_GQL_FIELD:
		case VOTES_GQL_FIELD:
		case WATCHES_GQL_FIELD:
		case RESOLUTION_GQL_FIELD:
		case STATUS_GQL_FIELD:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return transformAggFieldToLegacyField({
				[getValueKey(aggFieldChangeProps.__typename)]: fieldValue,
				__typename: aggFieldChangeProps.__typename,
				type: aggFieldChangeProps.type,
			} as AggField)?.value;

		case RICH_TEXT_GQL_FIELD:
			return transformAggRichTextFieldValueToLegacyFieldValue(
				fieldValue,
				aggFieldChangeProps.renderer,
			);
		case PARENT_GQL_FIELD:
			return transformAggParentFieldValueToLegacyFieldValue(fieldValue);
		case SPRINT_GQL_FIELD:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return transformAggFieldToLegacyField({
				__typename: aggFieldChangeProps.__typename,
				type: aggFieldChangeProps.type,
				...fieldValue,
			} as AggField)?.value;

		case TIME_TRACKING_GQL_FIELD:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return transformAggFieldToLegacyField({
				__typename: aggFieldChangeProps.__typename,
				type: aggFieldChangeProps.type,
				originalEstimate: fieldValue.originalEstimate,
				remainingEstimate: fieldValue.remainingEstimate,
				timeSpent: fieldValue.timeSpent,
			} as AggField)?.value;

		case CHECKBOX_GQL_FIELD:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return transformAggFieldToLegacyField({
				__typename: aggFieldChangeProps.__typename,
				type: aggFieldChangeProps.type,
				selectedOptions: fieldValue.selectedOptions,
				checkboxesFieldOptions: fieldValue.fieldOptions,
			} as AggField)?.value;

		case COMPONENTS_GQL_FIELD:
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			return transformAggFieldToLegacyField({
				__typename: aggFieldChangeProps.__typename,
				type: aggFieldChangeProps.type,
				selectedComponentsConnection: fieldValue.selectedComponentsConnection,
			} as AggField)?.value;

		default:
			throw new Error('__typename not implemented');
	}
};

const getValueKey = (__typename: string) => {
	const VALUE_KEYS: { [key: string]: string } = {
		[TEXT_GQL_FIELD]: 'text',
		[NUMBER_GQL_FIELD]: 'number',
		[PRIORITY_GQL_FIELD]: 'priority',
		[USER_PICKER_GQL_FIELD]: 'user',
		[DATE_PICKER_GQL_FIELD]: 'date',
		[PROJECT_GQL_FIELD]: 'project',
		[DATE_TIME_GQL_FIELD]: 'dateTime',
		[ORIGINAL_ESTIMATE_GQL_FIELD]: 'originalEstimate',
		[URL_GQL_FIELD]: 'uri',
		[SECURITY_GQL_FIELD]: 'securityLevel',
		[PEOPLE_GQL_FIELD]: 'selectedUsersConnection',
		[WATCHES_GQL_FIELD]: 'watch',
		[VOTES_GQL_FIELD]: 'vote',
		[COLOR_GQL_FIELD]: 'color',
		[MULTI_USER_PICKER_GQL_FIELD]: 'selectedUsersConnection',
		[ISSUE_TYPE_GQL_FIELD]: 'issueType',
		[RESOLUTION_GQL_FIELD]: 'resolution',
		[MULTI_GROUP_PICKER_GQL_FIELD]: 'selectedGroupsConnection',
		[SINGLE_GROUP_PICKER_GQL_FIELD]: 'selectedGroup',
		[TEAM_VIEW_GQL_FIELD]: 'selectedTeam',
		[STATUS_GQL_FIELD]: 'status',
		[RADIO_GQL_FIELD]: 'selectedOption',
		[RICH_TEXT_GQL_FIELD]: 'richText',
	};
	const value = VALUE_KEYS[__typename];
	if (value) {
		return value;
	}
	throw new Error(
		`Did you forget to implement getValueKey for your field type? Couldn't find a value in the map for ${__typename}`,
	);
};
